import { useMemo } from 'react'
import usePersonaPresenceRange from './usePersonaPresenceRange'

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// Hook for the `PersonaPresenceReports` collection scoped to a single
// (month, year). Used by both `DashboardPersonasCard` and the live-report
// `PersonasPresenceWidget`.
//
// Het ophalen zelf staat in `usePersonaPresenceRange`, samen met de
// jaarvariant — één query-recept, twee periodes.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook

/**
 * Eén maand, met de laadstand erbij.
 *
 * @param {number|null|undefined} month - 1-12, of null zolang dat nog niet bekend is
 * @param {number|null|undefined} year - 4-cijferig jaartal, of null
 * @param {{ enabled?: boolean }} [options]
 * @returns {import('./usePersonaPresenceRange').PresenceRangeResult}
 */
const usePersonaPresenceMonth = (month, year, { enabled = true } = {}) => {
    const bounds = useMemo(() => {
        if (!month || !year) return { start: null, end: null }
        return {
            start: new Date(year, month - 1, 1).getTime(),
            end: new Date(year, month, 0, 23, 59, 59).getTime(),
        }
    }, [month, year])

    return usePersonaPresenceRange(bounds.start, bounds.end, { enabled })
}

/**
 * Dezelfde maand, maar alleen de documenten. Blijft bestaan omdat de
 * live-report-widget een kale lijst verwacht.
 *
 * @param {number|null|undefined} month - 1-12, or null when not yet known
 * @param {number|null|undefined} year - 4-digit year, or null
 * @param {{ enabled?: boolean }} [options] - When `enabled` is false the
 *   hook returns an empty array and skips the network call. Useful for
 *   widget renderers that want to short-circuit in template-preview or
 *   module-disabled states without losing referential stability.
 * @returns {Array<{id: string, [key: string]: any}>}
 */
const usePersonaPresenceReports = (month, year, options) =>
    usePersonaPresenceMonth(month, year, options).reports

export default usePersonaPresenceReports
export { usePersonaPresenceMonth }
